import config from "#src/config";
import {
  AssertIsBeforeDate,
  AssertIsBetweenDate,
  AssertIsSameDate,
} from "#utils/assert";
import { InputIsMatched } from "#utils/filters";
import { SortListByType } from "#utils/sorters";
import { getColorPalette } from "#utils/styleCalculator";
import { getTimeStringFromDate } from "#utils/timeFormatter";
import { IconVariants } from "@validereinc/common-components";
import {
  WorkflowStepType,
  WorkflowTaskTypesType,
  WorkflowTriggerEventType,
  WorkflowTriggerType,
  WorkflowTriggerTypeType,
} from "@validereinc/domain";
import { filter, find, intersectionBy } from "es-toolkit/compat";
import moment from "moment";

export function filterWorkflowTasks(inputs, list, haveWorkflows) {
  // filter out tasks that is not contains filtered propertes
  const filteredWorkflowTasks = filter(list, function (workflowTasks) {
    if (!InputIsMatched(inputs.name, workflowTasks.name)) {
      return false;
    }

    const testTypeFilter = find(inputs.testTypes, {
      id: workflowTasks.test_type,
    });
    if (inputs.testTypes.length > 0 && !testTypeFilter) {
      return false;
    }

    const statusFilter = find(inputs.status, {
      id: workflowTasks.state,
    });

    if (inputs.status.length > 0 && !statusFilter) {
      return false;
    }

    const workflowsFilter = find(inputs.workflows, {
      id: workflowTasks.workflow_id,
    });
    if (inputs.workflows.length > 0 && !workflowsFilter && haveWorkflows) {
      return false;
    }

    const streamsFilter = find(inputs.streams, {
      id: workflowTasks.stream_id,
    });
    if (inputs.streams.length > 0 && !streamsFilter) {
      return false;
    }

    const siteFilter = intersectionBy(inputs.sites, workflowTasks.sites, "id");
    // @ts-expect-error legacy opshub typing
    if (inputs.sites.length > 0 && !siteFilter.length > 0) {
      return false;
    }

    return true;
  });

  return filteredWorkflowTasks;
}

export function getDateRange(date, view) {
  let from;
  let to;

  if (view === "monthly") {
    from = getTimeStringFromDate(
      moment(date).startOf("month"),
      config.DATE_FORMAT
    );
    to = getTimeStringFromDate(moment(from).endOf("month"), config.DATE_FORMAT);
  }

  return { from, to };
}

export const getWorkflowTasksWithCalibrationTasks = (
  workflowTasks,
  instruments,
  from,
  to
) => {
  const calibrationTasks = getInstrumentCalibrationTasks(instruments, from, to);
  return [...workflowTasks, ...calibrationTasks];
};

export const getInstrumentCalibrationTasks = (instruments, from, to) => {
  return instruments.reduce((instrumentTasks, instrument) => {
    if (
      instrument.next_calibration_date &&
      AssertIsBetweenDate(instrument.next_calibration_date, from, to, "day")
    ) {
      const calibrationDateEpoch = moment(instrument.next_calibration_date)
        .unix()
        .valueOf();
      return [
        ...instrumentTasks,
        {
          name: instrument.name,
          deadline_at_utc_epoch: calibrationDateEpoch,
          inserted_at_utc_epoch: calibrationDateEpoch,
          tests_required: ["calibrations"],
          state: getInstrumentCalibrationStatus(
            instrument.next_calibration_date
          ),
          sites: [{ name: instrument.location, id: instrument.site_id }],
          workflow_id: "calibration",
          test_type: "calibration",
          stream_id: "calibration",
          samples: [],
          note_count: 0,
        },
      ];
    } else {
      return instrumentTasks;
    }
  }, []);
};

const getInstrumentCalibrationStatus = (calibrationDate) => {
  // if the calibration next date is today then we set the status to be pending
  // if the calibration next date is late then we set the status to be late
  // otherwise its scheduled
  const today = moment();
  if (AssertIsSameDate(calibrationDate, today, "day")) {
    return "pending";
  } else if (AssertIsBeforeDate(calibrationDate, today, "day")) {
    return "late";
  } else {
    return "scheduled";
  }
};

export const getOptions = (workflows, sites, streams) => {
  workflows = [
    ...workflows,
    { id: "calibration", name: "Calibrations", test_type: "calibration" },
  ];
  streams = [
    ...streams,
    {
      stream_id: "calibration",
      name: "Calibrations",
      test_type: "calibration",
    },
  ];

  workflows = SortListByType(workflows, "name", "asc");
  streams = SortListByType(streams, "name", "asc");
  sites = SortListByType(sites, "name", "asc");

  const workflowsColorPalette = getColorPalette(workflows.length);
  const streamsColorPalette = getColorPalette(streams.length);

  workflows.forEach((workflow, index) => {
    workflow.color = workflowsColorPalette[index];
  });

  const streamOptions = streams.map((stream, index) => ({
    ...stream,
    id: stream.stream_id,
    color: streamsColorPalette[index],
  }));

  return { workflows, sites, streams: streamOptions };
};

export const getNumWeeksInMonth = (date) => {
  const daysInMonth = moment(date).daysInMonth();
  const firstDayOfMonth = moment(date).startOf("month").day();
  const lastDayOfMonth = moment(date).endOf("month").day();

  // weekdays given a number from 0-6, sunday: 0 ... saturday: 6
  const numDaysInFirstWeek = 7 - firstDayOfMonth;
  const numDaysInLastWeek = lastDayOfMonth + 1;

  /**
   * Usually 5 weeks in a month except for two cases:
   * 1) 28 days in month and 1st day is Sunday (meaning 4 full weeks only)
   * 2) >28 days in month and last week does not have enough days to finish month
   *    e.g. 31 days in month, 2 days in last week, 31 - 28 - 2 = 1 day leftover
   *    which must be by itself in the first week
   */
  let numWeeksInMonth = 5;
  if (daysInMonth === 28) {
    if (numDaysInFirstWeek === 7) {
      numWeeksInMonth = 4;
    }
  } else {
    const numDaysOver28 = daysInMonth % 7;
    const leftoverDays = numDaysOver28 - numDaysInLastWeek;

    if (leftoverDays > 0) {
      numWeeksInMonth = 6;
    }
  }

  return numWeeksInMonth;
};

export const getWorkflowTaskIconFromType = (
  type?: WorkflowTaskTypesType | typeof WorkflowStepType.PARALLEL
): IconVariants => {
  const defaultIcon: IconVariants = "validere";
  if (!type) return defaultIcon;
  if (type === WorkflowStepType.PARALLEL) return "arrows-split";

  const stepTypeToIconMap: Record<WorkflowTaskTypesType, IconVariants> = {
    create_event: "plus-clock-offset",
    form_choice: "clipboard-check",
    variable_choice: "bezier-curve",
    delay: "clock-countdown",
    create_form: "clipboard-plus",
    lock_record: "file-lock-simple",
    unlock_record: "file-unlock-simple",
    lock_report: "presentation-lock-simple",
    unlock_report: "presentation-unlock-simple",
    run_calculations: "calculator",
    run_templated_report: "presentation-plus",
    run_default_record_value_configuration: "file-play",
    manual_task: "wrench",
    submit_form: "clipboard-text",
    complete_event: "clock",
    choice: "check-square-offset",
    assess_templated_report_for_comments: "comment-plus",
    submit_dataset: "tray-arrow-up",
    update_custom_attributes: "clipboard-plus",
    update_attributes: "clipboard-plus",
    send_report: "envelope-presentation",
    send_notification: "envelope",
    ctr_consensus_choice: "test-tube",
    run_worker: "cpu",
    generic_system_action: "plugs-connected",
  };
  return stepTypeToIconMap?.[type] ?? defaultIcon;
};

export const getWorkflowTaskNameFromType = (
  type?: WorkflowTaskTypesType | typeof WorkflowStepType.PARALLEL
): string => {
  if (!type) return "";
  if (type === WorkflowStepType.PARALLEL) return "Parallel Branches";
  const stepTypeToTitleMap: Record<WorkflowTaskTypesType, string> = {
    create_event: "Create Event",
    form_choice: "Form Choice",
    variable_choice: "Variable Choice",
    delay: "Delay",
    create_form: "Create Form",
    lock_record: "Lock Records",
    unlock_record: "Unlock Records",
    lock_report: "Lock Report",
    unlock_report: "Unlock Report",
    run_calculations: "Run Calculations",
    run_templated_report: "Run Templated Report",
    run_default_record_value_configuration: "Run Default Automation",
    manual_task: "Manual Task",
    submit_form: "Submit Form",
    complete_event: "Complete Event",
    choice: "Choice",
    assess_templated_report_for_comments:
      "Assess Templated Report for Comments",
    submit_dataset: "Data Upload",
    update_custom_attributes: "Update Asset Custom Attributes",
    update_attributes: "Update Asset Attributes",
    send_report: "Send Report",
    send_notification: "Send Notification",
    ctr_consensus_choice: "CTR Consensus Choice",
    run_worker: "Run Worker",
    generic_system_action: "Generic System Action",
  };
  return stepTypeToTitleMap?.[type] ?? "";
};

export const getWorkflowTriggerIconFromTrigger = (
  trigger:
    | Exclude<WorkflowTriggerType, "dummy">
    | {
        type: WorkflowTriggerTypeType;
        event: WorkflowTriggerEventType | undefined;
      }
): IconVariants => {
  if (trigger.type === "scheduled") return "clock";

  const systemTriggersToIconMap: Record<
    WorkflowTriggerEventType,
    IconVariants
  > = {
    asset_create: "plus-circle",
    asset_update: "pencil-simple",
    form_submission_create: "clipboard-text",
    workflow_complete: "list-checks",
    dataset_submission: "tray-arrow-up",
  };

  return trigger.event
    ? ((systemTriggersToIconMap?.[trigger.event] as IconVariants) ?? "validere")
    : "validere";
};

export const getWorkflowTriggerNameFromTrigger = (
  trigger:
    | Exclude<WorkflowTriggerType, "dummy">
    | {
        type: WorkflowTriggerTypeType;
        event: WorkflowTriggerEventType | undefined;
      },
  isForNewTriggerDrawer?: boolean
): string => {
  if (trigger.type === "scheduled") return "Scheduled";

  const systemTriggersToNameMap: Record<WorkflowTriggerEventType, string> = {
    asset_create: "Asset Creation",
    asset_update: "Asset Update",
    form_submission_create: "Form Submission",
    workflow_complete: isForNewTriggerDrawer
      ? "Workflow Completion"
      : "Other Workflow Completion",
    dataset_submission: "Dataset Submission",
  };

  return trigger.event
    ? ((systemTriggersToNameMap?.[trigger.event] ??
        "Miscellaneous Trigger") as string)
    : "Miscellaneous Trigger";
};

export const getWorkflowTriggerDescriptionFromTrigger = (
  trigger:
    | Exclude<WorkflowTriggerType, "dummy">
    | {
        type: WorkflowTriggerTypeType;
        event: WorkflowTriggerEventType | undefined;
      }
): string => {
  if (trigger.type === "scheduled") return "Trigger on a set schedule";

  const systemTriggersToIconMap: Record<WorkflowTriggerEventType, string> = {
    asset_create: "Trigger on asset creation",
    asset_update: "Trigger on asset update",
    form_submission_create: "Trigger on form submission",
    workflow_complete: "Trigger on another workflow completion",
    dataset_submission: "Trigger on dataset submission",
  };

  return trigger.event
    ? ((systemTriggersToIconMap?.[trigger.event] as string) ?? "")
    : "";
};
